import * as React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
import { DataGrid, esES } from '@mui/x-data-grid'
import {
  getCompleteFeatureByID,
  getTreeAdminList,
  updateStatusTreeRegister,
} from '../../../services'
import { fetchAllTenants } from '../../../services/tenantService'
import { show, codeTree } from '../../../redux/slices/Drawer/drawerRight'
import {
  features as setTreeFeatureCollection,
  invalidateMapData,
} from '../../../redux/slices/Tree/treeFeatures'
import {
  clearFilteredData,
  setEnabledFilter,
} from '../../../redux/slices/Search/input'
import { getColumns } from './columns'
import SearchFilter from './SearchFilter'
import DrawerRightInfo from '../../DrawerRightInfo'
import DeleteTreeModal from './DeleteTreeModal'
import { usePersistedState } from '../../../hooks/usePersistedState'

const DEFAULT_FILTERS = { codes: '', tenantId: '', habilitado: 'all', correccionAplicada: 'all' }
const PAGE_SIZE_OPTIONS = [25, 50, 100]
const DRAWER_TRIGGER_FIELDS = new Set([
  'codigo',
  'tenantNombre',
  'especieNombre',
  'sitioNombre',
  'fechaRegistro',
])

const getAttributeValue = (attributes, key) =>
  attributes?.find(attr => attr?.metaFeature?.key === key)?.value

const getGeometryFromProperties = properties => {
  const lng = Number(getAttributeValue(properties?.attributes, 'longX'))
  const lat = Number(getAttributeValue(properties?.attributes, 'latY'))

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
  return { type: 'Point', coordinates: [lng, lat] }
}

const getCoordinatesFromFeature = feature => {
  const geometry = feature?.geometry || getGeometryFromProperties(feature?.properties)
  const lng = Number(geometry?.coordinates?.[0])
  const lat = Number(geometry?.coordinates?.[1])

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return { lat: null, lng: null }
  return { lat, lng }
}

const normalizeTreeFeature = (feature, tree) => {
  const properties = {
    ...(feature?.properties || {}),
    id: feature?.properties?.id ?? tree.uuid,
    code: feature?.properties?.code ?? tree.codigo,
  }

  return {
    ...feature,
    type: feature?.type || 'Feature',
    geometry: feature?.geometry || getGeometryFromProperties(properties),
    properties,
  }
}

const createDetailRow = (tree, coordinates) => ({
  ...tree,
  id: `${tree.uuid}_detail`,
  _isDetail: true,
  coordinates,
})

const TreeTable = () => {
  const dispatch = useDispatch()
  const [rows, setRows] = React.useState([])
  const [rowCount, setRowCount] = React.useState(0)
  const [page, setPage] = usePersistedState('admin.tree.page', 0)
  const [pageSize, setPageSize] = usePersistedState('admin.tree.pageSize', 25)
  const [filters, setFilters] = usePersistedState('admin.tree.filters', DEFAULT_FILTERS)
  const [loading, setLoading] = React.useState(false)
  const [expandedRows, setExpandedRows] = React.useState(new Set())
  const [coordinatesByUuid, setCoordinatesByUuid] = React.useState({})
  const [copyMessageOpen, setCopyMessageOpen] = React.useState(false)
  const [copySnackbarMessage, setCopySnackbarMessage] = React.useState('')
  const [detailLoadingUuid, setDetailLoadingUuid] = React.useState(null)
  const [detailErrorOpen, setDetailErrorOpen] = React.useState(false)
  const [selectedRowUuid, setSelectedRowUuid] = React.useState(null)
  const [selectedTreeIds, setSelectedTreeIds] = React.useState(() => new Set())
  const [statusDialog, setStatusDialog] = React.useState(null)
  const [statusUpdating, setStatusUpdating] = React.useState(false)
  const [statusFeedback, setStatusFeedback] = React.useState({
    open: false,
    severity: 'success',
    message: '',
  })
  const [tenantNamesById, setTenantNamesById] = React.useState({})
  const tableContainerRef = React.useRef(null)
  const deleteModalRef = React.useRef(null)

  const handleRequestDelete = React.useCallback(tree => {
    deleteModalRef.current?.openModal(tree)
  }, [])

  const { isStored } = useSelector(state => state.alertsGlobal)
  const treeFeatureData = useSelector(state => state.treeFeatures.data)
  const drawerRightOpen = useSelector(state => state.drawerRight.value)
  const visibleTreeRows = React.useMemo(() => rows.filter(row => !row._isDetail), [rows])
  const visibleTreeIds = React.useMemo(
    () => visibleTreeRows.map(row => row.uuid).filter(Boolean),
    [visibleTreeRows],
  )
  const selectedVisibleCount = React.useMemo(
    () => visibleTreeIds.filter(uuid => selectedTreeIds.has(uuid)).length,
    [selectedTreeIds, visibleTreeIds],
  )
  const visibleSelectionState = React.useMemo(
    () => ({
      total: visibleTreeIds.length,
      all: visibleTreeIds.length > 0 && selectedVisibleCount === visibleTreeIds.length,
      some: selectedVisibleCount > 0 && selectedVisibleCount < visibleTreeIds.length,
    }),
    [selectedVisibleCount, visibleTreeIds.length],
  )
  const hasBulkFilter = Boolean(filters.tenantId || filters.codes.trim())

  const buildParams = React.useCallback((pg, ps, f) => {
    const params = { page: pg, limit: ps }
    const codeList = f.codes
      .split(/[,\s]+/)
      .map(c => c.trim())
      .filter(Boolean)
    if (codeList.length) params.codes = codeList.join(',')
    if (f.tenantId) params.tenantId = f.tenantId
    if (f.habilitado !== 'all') params.habilitado = f.habilitado
    if (f.correccionAplicada && f.correccionAplicada !== 'all') {
      params.correccionAplicada = f.correccionAplicada
    }
    return params
  }, [])

  const loadData = React.useCallback(
    async (pg, ps, f) => {
      setLoading(true)
      const result = await getTreeAdminList(buildParams(pg, ps, f))
      setLoading(false)
      if (!result?.error) {
        const baseRows = result.trees || []
        const withDetail = []
        baseRows.forEach(t => {
          withDetail.push({ ...t, _isDetail: false })
          if (expandedRows.has(t.uuid)) {
            withDetail.push(createDetailRow(t, coordinatesByUuid[t.uuid]))
          }
        })
        setRows(withDetail)
        setRowCount(result.total || 0)
      }
    },
    [coordinatesByUuid, expandedRows, buildParams],
  )

  React.useEffect(() => {
    loadData(page, pageSize, filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, filters])

  React.useEffect(() => {
    if (isStored?.stored) loadData(page, pageSize, filters)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStored])

  // Reencuadrar si la página persistida quedó fuera de rango tras borrados.
  React.useEffect(() => {
    if (page > 0 && rowCount > 0 && page * pageSize >= rowCount) setPage(0)
  }, [rowCount, page, pageSize, setPage])

  React.useEffect(() => {
    let active = true

    fetchAllTenants().then(tenants => {
      if (!active) return

      const namesById = (tenants || []).reduce((acc, tenant) => {
        const id = tenant.id ?? tenant.id_t ?? tenant.tenantId
        if (id == null) return acc
        acc[String(id)] =
          tenant.fullName ||
          tenant.nombre_completo ||
          tenant.nombreCompleto ||
          tenant.name ||
          tenant.nombre ||
          ''
        return acc
      }, {})

      setTenantNamesById(namesById)
    })

    return () => {
      active = false
    }
  }, [])

  React.useEffect(() => {
    if (!drawerRightOpen) setSelectedRowUuid(null)
  }, [drawerRightOpen])

  React.useEffect(() => {
    setSelectedTreeIds(new Set())
  }, [page, pageSize, filters])

  React.useEffect(() => {
    const container = tableContainerRef.current
    if (!container) return

    let currentRow = null

    const clearHighlight = () => {
      currentRow?.classList.remove('trigger-hover-row')
      currentRow = null
    }

    const handleMouseOver = event => {
      const cell = event.target.closest('[data-field]')
      if (!cell || !DRAWER_TRIGGER_FIELDS.has(cell.getAttribute('data-field'))) {
        clearHighlight()
        return
      }
      const row = cell.closest('.MuiDataGrid-row')
      if (!row || row === currentRow) return
      clearHighlight()
      row.classList.add('trigger-hover-row')
      currentRow = row
    }

    container.addEventListener('mouseover', handleMouseOver)
    container.addEventListener('mouseleave', clearHighlight)

    return () => {
      container.removeEventListener('mouseover', handleMouseOver)
      container.removeEventListener('mouseleave', clearHighlight)
    }
  }, [])

  const loadCoordinates = React.useCallback(async uuid => {
    if (!uuid) return

    setCoordinatesByUuid(prev => ({
      ...prev,
      [uuid]: { ...(prev[uuid] || {}), loading: true, error: false },
    }))

    try {
      const fullFeature = await getCompleteFeatureByID(uuid)
      const coordinates = getCoordinatesFromFeature(fullFeature)
      setCoordinatesByUuid(prev => ({
        ...prev,
        [uuid]: { ...coordinates, loading: false, error: false, loaded: true },
      }))
    } catch {
      setCoordinatesByUuid(prev => ({
        ...prev,
        [uuid]: { ...(prev[uuid] || {}), loading: false, error: true, loaded: true },
      }))
    }
  }, [])

  const handleToggleExpand = React.useCallback(uuid => {
    const isExpanded = expandedRows.has(uuid)
    const coordinates = coordinatesByUuid[uuid]

    if (!isExpanded && !coordinates?.loading && !coordinates?.loaded) {
      loadCoordinates(uuid)
    }

    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }, [coordinatesByUuid, expandedRows, loadCoordinates])

  // Re-build rows when expandedRows changes without refetching
  React.useEffect(() => {
    setRows(prev => {
      const base = prev.filter(r => !r._isDetail)
      const withDetail = []
      base.forEach(t => {
        withDetail.push(t)
        if (expandedRows.has(t.uuid)) {
          withDetail.push(createDetailRow(t, coordinatesByUuid[t.uuid]))
        }
      })
      return withDetail
    })
  }, [coordinatesByUuid, expandedRows])

  const handleFilterChange = newFilters => {
    setPage(0)
    setFilters(newFilters)
  }

  const handleClearFilters = () => {
    setPage(0)
    setFilters(DEFAULT_FILTERS)
  }

  const handleOpenDrawer = React.useCallback(
    async tree => {
      if (!tree?.uuid || detailLoadingUuid === tree.uuid) return

      setSelectedRowUuid(tree.uuid)
      setDetailLoadingUuid(tree.uuid)
      try {
        const fullFeature = await getCompleteFeatureByID(tree.uuid)
        const normalizedFeature = normalizeTreeFeature(fullFeature, tree)
        const currentFeatures = treeFeatureData?.features || []
        const existingIndex = currentFeatures.findIndex(feature => {
          const properties = feature?.properties || {}
          return (
            String(properties.id) === String(normalizedFeature.properties.id) ||
            String(properties.code) === String(normalizedFeature.properties.code)
          )
        })
        const nextFeatures = [...currentFeatures]
        const featureIndex = existingIndex >= 0 ? existingIndex : nextFeatures.length

        if (existingIndex >= 0) {
          nextFeatures[existingIndex] = {
            ...nextFeatures[existingIndex],
            ...normalizedFeature,
            geometry: normalizedFeature.geometry || nextFeatures[existingIndex].geometry,
            properties: {
              ...(nextFeatures[existingIndex].properties || {}),
              ...normalizedFeature.properties,
            },
          }
        } else {
          nextFeatures.push(normalizedFeature)
        }

        dispatch(
          setTreeFeatureCollection({
            ...(treeFeatureData || {}),
            type: 'FeatureCollection',
            features: nextFeatures,
          }),
        )
        dispatch(codeTree(featureIndex))
        dispatch(show(true))
      } catch {
        setDetailErrorOpen(true)
      } finally {
        setDetailLoadingUuid(null)
      }
    },
    [detailLoadingUuid, dispatch, treeFeatureData],
  )

  const handleCellClick = React.useCallback(
    (params, event) => {
      if (params.row?._isDetail) return
      if (!DRAWER_TRIGGER_FIELDS.has(params.field)) return

      const target = event?.target
      const isInteractiveClick = target?.closest?.(
        'button, a, input, textarea, select, [role="button"]',
      )

      if (isInteractiveClick) return
      setSelectedRowUuid(params.row.uuid)
      handleOpenDrawer(params.row)
    },
    [handleOpenDrawer],
  )

  const handleToggleSelect = React.useCallback(uuid => {
    if (!uuid) return
    setSelectedTreeIds(prev => {
      const next = new Set(prev)
      if (next.has(uuid)) next.delete(uuid)
      else next.add(uuid)
      return next
    })
  }, [])

  const handleToggleSelectVisible = React.useCallback(() => {
    setSelectedTreeIds(prev => {
      const next = new Set(prev)
      if (visibleSelectionState.all) {
        visibleTreeIds.forEach(uuid => next.delete(uuid))
      } else {
        visibleTreeIds.forEach(uuid => next.add(uuid))
      }
      return next
    })
  }, [visibleSelectionState.all, visibleTreeIds])

  const openStatusDialog = React.useCallback((scope, enabled) => {
    const count = scope === 'selected' ? selectedTreeIds.size : rowCount
    if (!count) return
    setStatusDialog({ scope, enabled, count })
  }, [rowCount, selectedTreeIds.size])

  const getRowsToUpdate = React.useCallback(
    async (scope, enabled) => {
      if (scope === 'selected') {
        return visibleTreeRows.filter(
          row => selectedTreeIds.has(row.uuid) && row.habilitado !== enabled,
        )
      }

      const result = await getTreeAdminList(buildParams(0, Math.max(rowCount, 1), filters))
      if (result?.error) throw new Error(result.message || 'No se pudieron obtener los registros filtrados.')

      return (result.trees || []).filter(row => row.uuid && row.habilitado !== enabled)
    },
    [buildParams, filters, rowCount, selectedTreeIds, visibleTreeRows],
  )

  const handleConfirmStatusChange = React.useCallback(async () => {
    if (!statusDialog) return

    setStatusUpdating(true)
    try {
      const rowsToUpdate = await getRowsToUpdate(statusDialog.scope, statusDialog.enabled)
      const ids = rowsToUpdate.map(row => row.uuid)

      if (!ids.length) {
        setStatusFeedback({
          open: true,
          severity: 'info',
          message: 'No hay registros que cambiar con el estado solicitado.',
        })
        setStatusDialog(null)
        return
      }

      const results = await Promise.allSettled(
        ids.map(uuid =>
          updateStatusTreeRegister(uuid, { habilitado: statusDialog.enabled }),
        ),
      )
      const failedIds = new Set()
      results.forEach((result, index) => {
        if (result.status === 'rejected' || result.value?.update === false) {
          failedIds.add(ids[index])
        }
      })
      const failed = results.filter(
        result => result.status === 'rejected' || result.value?.update === false,
      ).length
      const updated = ids.length - failed
      const successfulIds = ids.filter(id => !failedIds.has(id))

      if (successfulIds.length) {
        dispatch(setEnabledFilter(false))
        dispatch(clearFilteredData())
        dispatch(invalidateMapData())
      }

      setStatusFeedback({
        open: true,
        severity: failed ? 'warning' : 'success',
        message: failed
          ? `Se actualizaron ${updated} registro(s). ${failed} no se pudieron actualizar.`
          : `Se ${statusDialog.enabled ? 'habilitaron' : 'deshabilitaron'} ${updated} registro(s).`,
      })
      setSelectedTreeIds(new Set())
      setStatusDialog(null)
      loadData(page, pageSize, filters)
    } catch (error) {
      setStatusFeedback({
        open: true,
        severity: 'error',
        message: error.message || 'No se pudo actualizar el estado de los registros.',
      })
    } finally {
      setStatusUpdating(false)
    }
  }, [dispatch, filters, getRowsToUpdate, loadData, page, pageSize, statusDialog])

  const copyToClipboard = React.useCallback(async text => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', '')
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
    }
  }, [])

  const handleCopyUuid = React.useCallback(
    async uuid => {
      if (!uuid) return
      await copyToClipboard(uuid)
      setCopySnackbarMessage('UUID copiado')
      setCopyMessageOpen(true)
    },
    [copyToClipboard],
  )

  const handleCopyCode = React.useCallback(
    async code => {
      if (!code) return
      await copyToClipboard(code)
      setCopySnackbarMessage('Código copiado')
      setCopyMessageOpen(true)
    },
    [copyToClipboard],
  )

  const columns = React.useMemo(
    () =>
      getColumns({
        expandedRows,
        onToggleExpand: handleToggleExpand,
        onCopyUuid: handleCopyUuid,
        onCopyCode: handleCopyCode,
        tenantNamesById,
        selectedTreeIds,
        onToggleSelect: handleToggleSelect,
        onToggleSelectVisible: handleToggleSelectVisible,
        visibleSelectionState,
        onRequestDelete: handleRequestDelete,
      }),
    [
      expandedRows,
      handleCopyUuid,
      handleCopyCode,
      handleToggleExpand,
      handleToggleSelect,
      handleToggleSelectVisible,
      selectedTreeIds,
      tenantNamesById,
      visibleSelectionState,
      handleRequestDelete,
    ],
  )

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          ml: 4,
          mb: 1,
        }}>
        <Typography
          sx={{
            fontWeight: 650,
            fontSize: '1em',
            color: '#6A6A6A',
            py: 0.75,
            mt: 1.5,
            mb: 0.375,
          }}>
          Administrar Árboles
        </Typography>
      </Box>

      <Box sx={{ px: 2 }}>
        <SearchFilter
          filters={filters}
          onChange={handleFilterChange}
          onClear={handleClearFilters}
        />
      </Box>

      <Box
        sx={{
          px: 2,
          pt: 1,
          pb: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {selectedTreeIds.size
            ? `${selectedTreeIds.size} registro(s) seleccionado(s)`
            : 'Selecciona registros para habilitar o deshabilitar'}
        </Typography>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedTreeIds.size || statusUpdating}
            onClick={() => openStatusDialog('selected', true)}>
            Habilitar seleccionados
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="warning"
            disabled={!selectedTreeIds.size || statusUpdating}
            onClick={() => openStatusDialog('selected', false)}>
            Deshabilitar seleccionados
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!hasBulkFilter || !rowCount || statusUpdating}
            onClick={() => openStatusDialog('filtered', true)}>
            Habilitar filtrados
          </Button>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={!hasBulkFilter || !rowCount || statusUpdating}
            onClick={() => openStatusDialog('filtered', false)}>
            Deshabilitar filtrados
          </Button>
        </Stack>
      </Box>

      {!hasBulkFilter && (
        <Box sx={{ px: 2, pb: 0.75 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Para acciones masivas filtradas, aplica un tenant o códigos específicos.
          </Typography>
        </Box>
      )}

      <Box
        ref={tableContainerRef}
        sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 2, display: 'flex' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={row => (row._isDetail ? row.id : row.uuid)}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          pageSize={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onPageChange={setPage}
          onPageSizeChange={newSize => {
            setPage(0)
            setPageSize(newSize)
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 64}
          getRowClassName={params =>
            [
              params.row._isDetail ? 'detail-row' : '',
              params.row.uuid === selectedRowUuid ? 'drawer-selected-row' : '',
            ]
              .filter(Boolean)
              .join(' ')
          }
          onCellClick={handleCellClick}
          disableSelectionOnClick
          sx={{
            flex: 1,
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
            '& .MuiDataGrid-row:not(.detail-row) .MuiDataGrid-cell': {
              py: 0.75,
              alignItems: 'center',
              whiteSpace: 'normal',
            },
            '& .MuiDataGrid-row:not(.detail-row) .MuiDataGrid-cell[data-field="codigo"], & .MuiDataGrid-row:not(.detail-row) .MuiDataGrid-cell[data-field="tenantNombre"], & .MuiDataGrid-row:not(.detail-row) .MuiDataGrid-cell[data-field="especieNombre"], & .MuiDataGrid-row:not(.detail-row) .MuiDataGrid-cell[data-field="sitioNombre"], & .MuiDataGrid-row:not(.detail-row) .MuiDataGrid-cell[data-field="fechaRegistro"]': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row:not(.detail-row):not(.drawer-selected-row):hover, & .MuiDataGrid-row:not(.detail-row).Mui-hovered:not(.drawer-selected-row)': {
              bgcolor: 'transparent',
            },
            '& .MuiDataGrid-row:not(.detail-row):not(.drawer-selected-row):hover .MuiDataGrid-cell': {
              bgcolor: 'transparent',
            },
            '& .MuiDataGrid-row.trigger-hover-row:not(.detail-row)': {
              bgcolor: '#F4F8FE',
            },
            '& .MuiDataGrid-row.trigger-hover-row:not(.detail-row) .MuiDataGrid-cell': {
              bgcolor: 'inherit',
            },
            '& .MuiDataGrid-row.trigger-hover-row.drawer-selected-row:not(.detail-row)': {
              bgcolor: '#DDEBFA',
            },
            '& .MuiDataGrid-row.drawer-selected-row:not(.detail-row)': {
              bgcolor: '#EAF3FF',
            },
            '& .MuiDataGrid-row.drawer-selected-row:not(.detail-row):hover': {
              bgcolor: '#EAF3FF',
            },
            '& .detail-row': {
              bgcolor: '#F9FAFC',
              borderBottom: '1px solid #E0E0E0',
              width: '100% !important',
              maxWidth: 'none !important',
            },
            '& .detail-row .MuiDataGrid-cell': {
              p: 0,
              alignItems: 'stretch',
              minWidth: '0 !important',
              maxWidth: 'none !important',
              overflow: 'visible !important',
            },
            '& .detail-row .MuiDataGrid-cell:not([data-field="_select"])': {
              display: 'none',
            },
            '& .detail-row .MuiDataGrid-cell[data-field="_select"]': {
              width: '100% !important',
              minWidth: '100% !important',
              maxWidth: 'none !important',
              flex: '1 0 100% !important',
              flexBasis: '100% !important',
            },
            '& .detail-row .MuiDataGrid-cell > *': {
              width: '100%',
              maxWidth: 'none',
              flex: '1 1 100%',
            },
            '& .detail-row .MuiDataGrid-cellContent': {
              width: '100%',
              maxWidth: 'none',
              overflow: 'visible',
            },
          }}
        />
      </Box>

      <DrawerRightInfo disableNavigationOnClose />

      <DeleteTreeModal ref={deleteModalRef} />

      <Dialog open={Boolean(statusDialog)} onClose={() => !statusUpdating && setStatusDialog(null)}>
        <DialogTitle>
          {statusDialog?.enabled ? 'Habilitar registros' : 'Deshabilitar registros'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {statusDialog?.scope === 'filtered'
              ? `Esta acción aplicará sobre los resultados filtrados (${statusDialog?.count || 0} registro(s)).`
              : `Esta acción aplicará sobre ${statusDialog?.count || 0} registro(s) seleccionado(s).`}
          </DialogContentText>
          <DialogContentText sx={{ mt: 1 }}>
            ¿Deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog(null)} disabled={statusUpdating}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={statusDialog?.enabled ? 'primary' : 'warning'}
            onClick={handleConfirmStatusChange}
            disabled={statusUpdating}
            startIcon={statusUpdating ? <CircularProgress size={16} color="inherit" /> : null}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copyMessageOpen}
        autoHideDuration={1800}
        onClose={() => setCopyMessageOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setCopyMessageOpen(false)}>
          {copySnackbarMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={detailErrorOpen}
        autoHideDuration={3000}
        onClose={() => setDetailErrorOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setDetailErrorOpen(false)}>
          No se pudo cargar el detalle del árbol
        </Alert>
      </Snackbar>

      <Snackbar
        open={statusFeedback.open}
        autoHideDuration={3200}
        onClose={() => setStatusFeedback(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert
          severity={statusFeedback.severity}
          variant="filled"
          onClose={() => setStatusFeedback(prev => ({ ...prev, open: false }))}>
          {statusFeedback.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default TreeTable
